import { useState } from 'react';
import {
  Card,
  Text,
  SimpleGrid,
  Group,
  Stack,
  Badge,
  Paper,
  ThemeIcon,
  Grid,
  Divider,
  ActionIcon,
} from '@mantine/core';
import {
  IconPlugConnected,
  IconDatabase,
  IconBuildingStore,
  IconActivity,
  IconArrowDown,
  IconArrowRight,
  IconMessageCircle,
  IconChartBar,
  IconChecklist,
  IconTrendingUp,
  IconBuilding,
  IconUsers,
  IconCash,
  IconClipboardData,
  IconNetwork,
  IconBulb,
  IconTarget,
  IconHistory,
} from '@tabler/icons-react';
import TopNav from '../components/TopNav';

// Panago Data Sources
const panagoDataSources = [
  { name: 'Franchise POS Systems', icon: IconBuildingStore },
  { name: 'Customer Order Data', icon: IconClipboardData },
  { name: 'Supply Chain Metrics', icon: IconNetwork },
  { name: 'Franchisee Performance', icon: IconUsers },
  { name: 'Digital Channel Analytics', icon: IconActivity },
];

// Phantom Screens Data Sources
const phantomDataSources = [
  { name: 'Dealer Network Data', icon: IconBuilding },
  { name: 'Manufacturing Metrics', icon: IconActivity },
  { name: 'Regional Sales Reports', icon: IconChartBar },
  { name: 'Inventory & Lead Times', icon: IconDatabase },
  { name: 'Customer Satisfaction', icon: IconUsers },
];

// Apex Food Source Data Sources
const apexDataSources = [
  { name: 'Client Contract Data', icon: IconClipboardData },
  { name: 'Production Line Metrics', icon: IconActivity },
  { name: 'Quality & Compliance', icon: IconChecklist },
  { name: 'Revenue by Segment', icon: IconCash },
];

// Holdco Data Sources
const holdcoDataSources = [
  { name: 'Consolidated Financials', icon: IconCash },
  { name: 'Board & Governance', icon: IconUsers },
  { name: 'Capital Allocation', icon: IconTrendingUp },
  { name: 'Market Intelligence', icon: IconBulb },
];

// Access Channels (Output Capabilities)
const accessChannels = [
  { name: 'Portfolio Overview', icon: IconChartBar, color: 'indigo' },
  { name: 'OpCo Dashboards', icon: IconBuilding, color: 'blue' },
  { name: 'Trend Analysis', icon: IconTrendingUp, color: 'teal' },
  { name: 'Decision Audit', icon: IconHistory, color: 'violet' },
  { name: 'Memory Graph', icon: IconNetwork, color: 'grape' },
  { name: 'AI Insights', icon: IconMessageCircle, color: 'pink' },
];

// Strategic Insights
const strategicInsights = [
  { name: 'Cross-OpCo Synergies', description: 'Identify opportunities across portfolio companies' },
  { name: 'Performance Benchmarks', description: 'Compare metrics against industry standards' },
  { name: 'Investment Decisions', description: 'Data-driven capital allocation recommendations' },
  { name: 'Risk Indicators', description: 'Early warning signals across all operations' },
];

export default function HowItWorksPage() {
  // Collapse/expand state for presentation mode - all start closed
  const [showDualPath, setShowDualPath] = useState(false);
  const [showStrategicLayer, setShowStrategicLayer] = useState(false);
  const [showAccessChannels, setShowAccessChannels] = useState(false);
  const [showInsights, setShowInsights] = useState(false);

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      flexDirection: 'column',
      background: '#f8f9fa'
    }}>
      {/* Top Navigation */}
      <TopNav />

      {/* Main Content Area */}
      <div style={{
        flex: 1,
        overflow: 'auto',
        padding: 24
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <Text size="xl" fw={700} mb="lg">How Zeus Memory Works for Kenbri Group</Text>

          <Stack gap="lg">
            {/* Header Description */}
            <Paper p="md" radius="md" bg="indigo.0" style={{ borderLeft: '4px solid var(--mantine-color-indigo-6)' }}>
              <Text size="sm">
                Zeus Memory serves as the central intelligence layer for Kenbri Group's family office operations,
                connecting data from all operating companies—Panago Pizza, Phantom Screens, and Apex Food Source—to
                deliver unified portfolio insights, strategic decision support, and institutional knowledge preservation.
              </Text>
            </Paper>

            {/* Level 1: Input Sources - All OpCos */}
            <Grid gutter="md">
              {/* Panago Data Sources */}
              <Grid.Col span={{ base: 12, md: 3 }}>
                <Card shadow="sm" padding="md" radius="md" withBorder style={{ borderColor: '#dc2626', borderWidth: 2 }}>
                  <Group gap="xs" mb="sm">
                    <div style={{ width: 28, height: 28, background: '#dc2626', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Text size="sm" c="white" fw={700}>P</Text>
                    </div>
                    <Text fw={600} size="md">Panago Pizza</Text>
                  </Group>
                  <Stack gap={8}>
                    {panagoDataSources.map((item) => (
                      <Group key={item.name} gap="xs" align="flex-start">
                        <ThemeIcon size="sm" color="red" variant="light" style={{ flexShrink: 0, marginTop: 2 }}>
                          <item.icon size={12} />
                        </ThemeIcon>
                        <Text size="xs" style={{ flex: 1 }}>{item.name}</Text>
                      </Group>
                    ))}
                  </Stack>
                </Card>
              </Grid.Col>

              {/* Phantom Data Sources */}
              <Grid.Col span={{ base: 12, md: 3 }}>
                <Card shadow="sm" padding="md" radius="md" withBorder style={{ borderColor: '#2563eb', borderWidth: 2 }}>
                  <Group gap="xs" mb="sm">
                    <div style={{ width: 28, height: 28, background: '#2563eb', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Text size="sm" c="white" fw={700}>PS</Text>
                    </div>
                    <Text fw={600} size="md">Phantom Screens</Text>
                  </Group>
                  <Stack gap={8}>
                    {phantomDataSources.map((item) => (
                      <Group key={item.name} gap="xs" align="flex-start">
                        <ThemeIcon size="sm" color="blue" variant="light" style={{ flexShrink: 0, marginTop: 2 }}>
                          <item.icon size={12} />
                        </ThemeIcon>
                        <Text size="xs" style={{ flex: 1 }}>{item.name}</Text>
                      </Group>
                    ))}
                  </Stack>
                </Card>
              </Grid.Col>

              {/* Apex Data Sources */}
              <Grid.Col span={{ base: 12, md: 3 }}>
                <Card shadow="sm" padding="md" radius="md" withBorder style={{ borderColor: '#16a34a', borderWidth: 2 }}>
                  <Group gap="xs" mb="sm">
                    <div style={{ width: 28, height: 28, background: '#16a34a', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Text size="sm" c="white" fw={700}>A</Text>
                    </div>
                    <Text fw={600} size="md">Apex Food Source</Text>
                  </Group>
                  <Stack gap={8}>
                    {apexDataSources.map((item) => (
                      <Group key={item.name} gap="xs" align="flex-start">
                        <ThemeIcon size="sm" color="green" variant="light" style={{ flexShrink: 0, marginTop: 2 }}>
                          <item.icon size={12} />
                        </ThemeIcon>
                        <Text size="xs" style={{ flex: 1 }}>{item.name}</Text>
                      </Group>
                    ))}
                  </Stack>
                </Card>
              </Grid.Col>

              {/* Holdco Data Sources */}
              <Grid.Col span={{ base: 12, md: 3 }}>
                <Card shadow="sm" padding="md" radius="md" withBorder style={{ borderColor: '#6366f1', borderWidth: 2 }}>
                  <Group gap="xs" mb="sm">
                    <div style={{ width: 28, height: 28, background: '#6366f1', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Text size="sm" c="white" fw={700}>R</Text>
                    </div>
                    <Text fw={600} size="md">Holdco / Corporate</Text>
                  </Group>
                  <Stack gap={8}>
                    {holdcoDataSources.map((item) => (
                      <Group key={item.name} gap="xs" align="flex-start">
                        <ThemeIcon size="sm" color="indigo" variant="light" style={{ flexShrink: 0, marginTop: 2 }}>
                          <item.icon size={12} />
                        </ThemeIcon>
                        <Text size="xs" style={{ flex: 1 }}>{item.name}</Text>
                      </Group>
                    ))}
                  </Stack>
                </Card>
              </Grid.Col>
            </Grid>

            {/* Arrow Down - Click to expand/collapse Dual Path */}
            <Group justify="center">
              <ActionIcon
                variant="subtle"
                size="xl"
                onClick={() => setShowDualPath(!showDualPath)}
                title={showDualPath ? "Collapse section" : "Expand section"}
                style={{
                  cursor: 'pointer',
                  transition: 'transform 0.3s ease',
                  transform: showDualPath ? 'rotate(0deg)' : 'rotate(-90deg)'
                }}
              >
                <IconArrowDown size={28} color="var(--mantine-color-gray-5)" />
              </ActionIcon>
            </Group>

            {/* Level 2: Dual Processing Paths */}
            {showDualPath && (
            <Group align="stretch" gap="md">
              {/* Left: Internal Intelligence (includes Zeus Memory Core) */}
              <Card
                shadow="md"
                padding="lg"
                radius="lg"
                withBorder
                style={{
                  borderColor: 'var(--mantine-color-indigo-6)',
                  borderWidth: 2,
                  background: 'linear-gradient(135deg, var(--mantine-color-indigo-0) 0%, white 100%)',
                  flex: showStrategicLayer ? 1 : 2,
                  transition: 'flex 0.3s ease',
                }}
              >
                <Group justify="center" gap="md">
                  <ThemeIcon size={48} radius="xl" color="indigo" variant="filled">
                    <IconPlugConnected size={28} />
                  </ThemeIcon>
                  <Stack gap={0}>
                    <Text fw={700} size="lg">Internal Intelligence</Text>
                    <Text size="sm" c="dimmed">Zeus Memory Core · Your Business Operations</Text>
                  </Stack>
                </Group>

                <Divider my="md" />

                <Group justify="center" gap="xs" mb="xs">
                  <Badge variant="dot" color="indigo" size="sm">Data Integration</Badge>
                  <Badge variant="dot" color="indigo" size="sm">Semantic Memory</Badge>
                  <Badge variant="dot" color="indigo" size="sm">Decision Tracking</Badge>
                  <Badge variant="dot" color="indigo" size="sm">Knowledge Graph</Badge>
                </Group>
                <Group justify="center" gap="xs">
                  <Badge variant="dot" color="teal" size="sm">Sales & Revenue</Badge>
                  <Badge variant="dot" color="teal" size="sm">Location Performance</Badge>
                  <Badge variant="dot" color="teal" size="sm">Customer Insights</Badge>
                  <Badge variant="dot" color="teal" size="sm">Operational KPIs</Badge>
                </Group>
              </Card>

              {/* Horizontal Arrow to Strategic Layer */}
              <Stack justify="center" align="center" gap={0}>
                <ActionIcon
                  variant="subtle"
                  size="xl"
                  onClick={() => setShowStrategicLayer(!showStrategicLayer)}
                  title={showStrategicLayer ? "Collapse Strategic Layer" : "Expand Strategic Layer"}
                  style={{
                    cursor: 'pointer',
                    transition: 'transform 0.3s ease',
                    transform: showStrategicLayer ? 'rotate(0deg)' : 'rotate(0deg)'
                  }}
                >
                  <IconArrowRight size={28} color={showStrategicLayer ? 'var(--mantine-color-violet-5)' : 'var(--mantine-color-gray-5)'} />
                </ActionIcon>
              </Stack>

              {/* Right: Vendor/Supplier Intelligence */}
              {showStrategicLayer && (
              <Card
                shadow="md"
                padding="lg"
                radius="lg"
                withBorder
                style={{
                  borderColor: 'var(--mantine-color-violet-5)',
                  borderWidth: 2,
                  background: 'linear-gradient(135deg, var(--mantine-color-violet-0) 0%, white 100%)',
                  flex: 1,
                }}
              >
                <Group justify="center" gap="md">
                  <ThemeIcon size={48} radius="xl" color="violet" variant="filled">
                    <IconTarget size={28} />
                  </ThemeIcon>
                  <Stack gap={0}>
                    <Text fw={700} size="lg">Vendor/Supplier Intelligence</Text>
                    <Text size="sm" c="dimmed">Collaborative Data Sharing</Text>
                  </Stack>
                </Group>

                <Divider my="md" />

                <Group justify="center" gap="xs">
                  <Badge variant="dot" color="violet" size="sm">Supplier Performance</Badge>
                  <Badge variant="dot" color="violet" size="sm">Shared Forecasting</Badge>
                  <Badge variant="dot" color="violet" size="sm">Negotiation Strength</Badge>
                  <Badge variant="dot" color="violet" size="sm">Accountability</Badge>
                </Group>
              </Card>
              )}
            </Group>
            )}

            {/* Arrows Down for both paths - side by side */}
            {showDualPath && (
            <Group align="flex-start" gap="md">
              {/* Left side: Arrow and Access Channels */}
              <Stack gap="lg" style={{ flex: showStrategicLayer ? 1 : 2, transition: 'flex 0.3s ease' }}>
                <Group justify="center">
                  <ActionIcon
                    variant="subtle"
                    size="xl"
                    onClick={() => setShowAccessChannels(!showAccessChannels)}
                    title={showAccessChannels ? "Collapse Access Channels" : "Expand Access Channels"}
                    style={{
                      cursor: 'pointer',
                      transition: 'transform 0.3s ease',
                      transform: showAccessChannels ? 'rotate(0deg)' : 'rotate(-90deg)'
                    }}
                  >
                    <IconArrowDown size={28} color="var(--mantine-color-teal-5)" />
                  </ActionIcon>
                </Group>

                {/* Access Channels */}
                {showAccessChannels && (
                <Card shadow="sm" padding="md" radius="md" withBorder>
                  <Text fw={600} size="sm" mb="md" ta="center">
                    Dashboard Access
                  </Text>
                  <SimpleGrid cols={{ base: 2, sm: 3, md: showStrategicLayer ? 3 : 3 }} spacing="sm">
                    {accessChannels.map((channel) => (
                      <Paper
                        key={channel.name}
                        shadow="xs"
                        p="sm"
                        radius="md"
                        withBorder
                        style={{
                          borderColor: `var(--mantine-color-${channel.color}-4)`,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '';
                        }}
                      >
                        <Stack align="center" gap="xs">
                          <ThemeIcon size="lg" color={channel.color} variant="light">
                            <channel.icon size={18} />
                          </ThemeIcon>
                          <Text size="xs" ta="center" fw={500} lineClamp={2}>
                            {channel.name}
                          </Text>
                        </Stack>
                      </Paper>
                    ))}
                  </SimpleGrid>
                </Card>
                )}
              </Stack>

              {/* Right side: Arrow and Strategic Insights */}
              {showStrategicLayer && (
              <Stack gap="lg" style={{ flex: 1 }}>
                <Group justify="center">
                  <ActionIcon
                    variant="subtle"
                    size="xl"
                    onClick={() => setShowInsights(!showInsights)}
                    title={showInsights ? "Collapse Insights" : "Expand Insights"}
                    style={{
                      cursor: 'pointer',
                      transition: 'transform 0.3s ease',
                      transform: showInsights ? 'rotate(0deg)' : 'rotate(-90deg)'
                    }}
                  >
                    <IconArrowDown size={28} color="var(--mantine-color-violet-5)" />
                  </ActionIcon>
                </Group>

                {/* Strategic Insights */}
                {showInsights && (
                <Card
                  shadow="sm"
                  padding="md"
                  radius="md"
                  withBorder
                  style={{
                    borderColor: 'var(--mantine-color-violet-3)',
                  }}
                >
                  <Text fw={600} size="sm" mb="md" ta="center" c="violet">
                    AI-Powered Insights
                  </Text>
                  <Stack gap="sm">
                    {strategicInsights.map((insight) => (
                    <Paper key={insight.name} p="sm" radius="md" bg="violet.0">
                      <Group gap="xs">
                        <ThemeIcon size="sm" color="violet" variant="light">
                          <IconBulb size={14} />
                        </ThemeIcon>
                        <Text size="xs" fw={500}>{insight.name}</Text>
                      </Group>
                      <Text size="xs" c="dimmed" mt={4}>
                        {insight.description}
                      </Text>
                    </Paper>
                    ))}
                  </Stack>
                </Card>
                )}
              </Stack>
              )}
            </Group>
            )}

            {/* Footer */}
            <Paper p="sm" radius="md" bg="gray.0">
              <Text size="xs" c="dimmed" ta="center">
                Zeus Memory connects all Kenbri Group operating companies, preserving institutional knowledge,
                tracking strategic decisions, and providing AI-powered insights to support the family office's
                long-term investment and operational objectives.
              </Text>
            </Paper>
          </Stack>
        </div>
      </div>
    </div>
  );
}
